import React, { useState, useEffect } from 'react';
import { gameAPI, registrationAPI, drinksAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const GamesTab = ({ 
  games, 
  setGames, 
  selectedGame, 
  setSelectedGame, 
  showAddGame, 
  setShowAddGame, 
  loading, 
  setLoading,
  loadGames
}) => {
  const [gameForm, setGameForm] = useState({
    opponent: '',
    date: '',
    time: '',
    location: 'EFC Buseck', // Standard-Wert, wird nicht mehr angezeigt
    category: 'bundesliga',
    description: '',
    maxRegistrations: 50
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedGames, setSelectedGames] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [processingRegistrations, setProcessingRegistrations] = useState(new Set());
  const [drinkTypes, setDrinkTypes] = useState([]);

  useEffect(() => {
    loadDrinks();
  }, []);

  const loadDrinks = async () => {
    try {
      const response = await drinksAPI.getAllDrinks();
      if (response.success && response.drinks) {
        setDrinkTypes(response.drinks.map(drink => ({
          value: drink.value,
          label: drink.name
        })));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Getränke:', error);
      // Fallback auf Standard-Getränke
      setDrinkTypes([
        { value: 'bier', label: 'Bier' },
        { value: 'aeppler', label: 'Äppler' },
        { value: 'cola', label: 'Cola' }
      ]);
    }
  };


  const handleApproveRegistration = async (registrationId, gameName) => {
    try {
      setProcessingRegistrations(prev => new Set([...prev, registrationId]));
      const response = await registrationAPI.updateRegistrationStatus(registrationId, 'confirmed');
      if (response.success) {
        toast.success(`Anmeldung für ${gameName} wurde genehmigt`);
        await loadGames();

        if (selectedGame) {
          const updatedGameResponse = await gameAPI.getGame(selectedGame.id);
          if (updatedGameResponse.success) {
            const updatedGame = {
              ...selectedGame,
              registrations: updatedGameResponse.game.registrations.map(reg => ({
                id: reg.id,
                userId: reg.user_id,
                name: reg.first_name && reg.last_name ?
                  `${reg.first_name} ${reg.last_name}` : reg.username || 'Unbekannt',
                email: reg.email || '',
                ticketType: reg.ticket_type,
                drink: reg.drink || 'bier',
                message: reg.message || '',
                registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
                status: reg.status || 'confirmed'
              }))
            };
            setSelectedGame(updatedGame);
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Genehmigen der Anmeldung:', error);
      toast.error('Fehler beim Genehmigen der Anmeldung');
    } finally {
      setProcessingRegistrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(registrationId);
        return newSet;
      });
    }
  };

  const handleRejectRegistration = async (registrationId, gameName) => {
    if (window.confirm('Möchten Sie diese Anmeldung wirklich ablehnen?')) {
      try {
        setProcessingRegistrations(prev => new Set([...prev, registrationId]));
        const response = await registrationAPI.updateRegistrationStatus(registrationId, 'cancelled');
        if (response.success) {
          toast.success(`Anmeldung für ${gameName} wurde abgelehnt`);
          await loadGames();

          if (selectedGame) {
            const updatedGameResponse = await gameAPI.getGame(selectedGame.id);
            if (updatedGameResponse.success) {
              const updatedGame = {
                ...selectedGame,
                registrations: updatedGameResponse.game.registrations.map(reg => ({
                  id: reg.id,
                  userId: reg.user_id,
                  name: reg.first_name && reg.last_name ?
                    `${reg.first_name} ${reg.last_name}` : reg.username || 'Unbekannt',
                  email: reg.email || '',
                  ticketType: reg.ticket_type,
                  drink: reg.drink || 'bier',
                  message: reg.message || '',
                  registeredAt: reg.created_at ? reg.created_at.split(' ')[0] : '',
                  status: reg.status || 'confirmed'
                }))
              };
              setSelectedGame(updatedGame);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Ablehnen der Anmeldung:', error);
        toast.error('Fehler beim Ablehnen der Anmeldung');
      } finally {
        setProcessingRegistrations(prev => {
          const newSet = new Set(prev);
          newSet.delete(registrationId);
          return newSet;
        });
      }
    }
  };

  const handleGameFormChange = (e) => {
    const { name, value } = e.target;
    setGameForm({
      ...gameForm,
      [name]: value
    });
  };

  const handleAddGame = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const gameData = {
        opponent: gameForm.opponent,
        gameDate: gameForm.date,
        gameTime: gameForm.time,
        location: gameForm.location,
        category: gameForm.category,
        description: gameForm.description,
        maxRegistrations: gameForm.maxRegistrations
      };

      const response = await gameAPI.createGame(gameData);
      if (response.success) {
        toast.success('Spiel wurde erfolgreich hinzugefügt!');
        setGameForm({
          opponent: '',
          date: '',
          time: '',
          location: 'EFC Buseck',
          category: 'bundesliga',
          description: '',
          maxRegistrations: 50
        });
        setShowAddGame(false);
        await loadGames();
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Spiels:', error);
      toast.error(error.message || 'Fehler beim Hinzufügen des Spiels');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (window.confirm('Möchten Sie dieses Spiel wirklich löschen?')) {
      try {
        const response = await gameAPI.deleteGame(gameId);
        if (response.success) {
          toast.success('Spiel wurde gelöscht!');
          if (selectedGame && selectedGame.id === gameId) {
            setSelectedGame(null);
          }
          await loadGames();
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Spiels:', error);
        toast.error(error.message || 'Fehler beim Löschen des Spiels');
      }
    }
  };

  const getTicketTypeLabel = (type) => {
    const types = {
      'stehplatz': 'Stehplatz',
      'sitzplatz': 'Sitzplatz',
      'ohne-ticket': 'Ohne Ticket'
    };
    return types[type] || type;
  };

  const getDrinkLabel = (drinks) => {
    if (!drinks) return 'Nicht angegeben';

    // Wenn es ein String ist (komma-separiert), zu Array konvertieren
    const drinksArray = typeof drinks === 'string'
      ? drinks.split(',').filter(d => d.trim())
      : (Array.isArray(drinks) ? drinks : []);

    if (drinksArray.length === 0) return 'Nicht angegeben';

    // Labels für jedes Getränk holen
    const labels = drinksArray.map(drinkValue => {
      const drinkType = drinkTypes.find(d => d.value === drinkValue.trim());
      return drinkType ? drinkType.label : drinkValue;
    });

    return labels.join(', ');
  };

  const generatePDF = (game) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    const isChampionsLeague = game.category === 'champions-league';

    doc.setFontSize(20);
    doc.text('EFC Buseck - Anmeldungsliste', 20, 30);

    doc.setFontSize(14);
    doc.text(`Spiel: ${game.opponent}`, 20, 50);
    doc.text(`Datum: ${new Date(game.date).toLocaleDateString('de-DE')} - ${game.time}`, 20, 65);
    doc.text(`Ort: ${game.location}`, 20, 80);
    doc.text(`Anmeldungen: ${game.registrations.length}/${game.maxRegistrations}`, 20, 95);

    if (isChampionsLeague) {
      doc.setFontSize(10);
      doc.setTextColor(255, 140, 0);
      doc.text('Champions League - Erweiterte Teilnehmerdaten', 20, 107);
      doc.setTextColor(0, 0, 0);
    }

    // Für Champions League: Alle Daten in einer Tabelle
    if (isChampionsLeague) {
      const tableData = game.registrations.map((reg, index) => [
        index + 1,
        `${reg.firstName || ''} ${reg.lastName || ''}`.trim() || reg.name,
        getTicketTypeLabel(reg.ticketType),
        reg.dateOfBirth ? new Date(reg.dateOfBirth).toLocaleDateString('de-DE') : '-',
        reg.placeOfBirth || '-',
        reg.address || '-',
        reg.idCardNumber || '-'
      ]);

      autoTable(doc, {
        head: [['#', 'Name', 'Ticket', 'Geb.Datum', 'Geburtsort', 'Adresse', 'Perso-Nr']],
        body: tableData,
        startY: 115,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [220, 20, 60],
          fontSize: 8,
          fontStyle: 'bold'
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto'
      });
    } else {
      // Standard-Tabelle für normale Spiele
      const tableData = game.registrations.map((reg, index) => [
        index + 1,
        reg.name,
        reg.email,
        getTicketTypeLabel(reg.ticketType),
        reg.message || '-',
        new Date(reg.registeredAt).toLocaleDateString('de-DE')
      ]);

      autoTable(doc, {
        head: [['#', 'Name', 'E-Mail', 'Ticket-Art', 'Nachricht', 'Angemeldet am']],
        body: tableData,
        startY: 110,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 139, 202] }
      });
    }

    doc.save(`Anmeldungsliste_${game.opponent}_${new Date(game.date).toLocaleDateString('de-DE')}.pdf`);
  };

  const generateExcel = (game) => {
    // Gruppiere Registrierungen nach user_id für Gruppenanmeldungen
    const groupedRegistrations = game.registrations.reduce((groups, registration) => {
      const userId = registration.userId || registration.user_id;
      if (!groups[userId]) {
        groups[userId] = [];
      }
      groups[userId].push(registration);
      return groups;
    }, {});

    let rowIndex = 0;
    const excelData = [];

    Object.values(groupedRegistrations).forEach((userRegistrations) => {
      const isGroup = userRegistrations.length > 1;

      userRegistrations.forEach((registration, groupIndex) => {
        rowIndex++;

        // Versuche firstName/lastName zu nutzen, falls vorhanden, sonst den kombinierten Namen aufteilen
        let firstName = registration.firstName || '';
        let lastName = registration.lastName || '';

        if (!firstName && !lastName && registration.name) {
          const nameParts = registration.name.split(' ');
          if (nameParts.length > 1) {
            firstName = nameParts.slice(0, -1).join(' ');
            lastName = nameParts[nameParts.length - 1];
          } else {
            firstName = registration.name;
          }
        }

        const rowData = {
          'Nr.': rowIndex,
          'Gruppe': isGroup ? `${groupIndex + 1}/${userRegistrations.length}` : '-',
          'Vorname': firstName,
          'Nachname': lastName,
          'E-Mail': registration.email,
          'Ticket-Art': getTicketTypeLabel(registration.ticketType),
          'Getränke': getDrinkLabel(registration.drink || ''),
          'Status': registration.status === 'pending' ? 'Ausstehend' :
                   registration.status === 'confirmed' ? 'Bestätigt' :
                   registration.status === 'cancelled' ? 'Abgelehnt' : registration.status,
          'Angemeldet am': registration.registeredAt ? new Date(registration.registeredAt).toLocaleDateString('de-DE') : '-'
        };

        // Champions League Zusatzfelder
        if (game.category === 'champions-league') {
          rowData['Adresse'] = registration.address || '-';
          rowData['Perso-Nr'] = registration.idCardNumber || '-';
          rowData['Geb. Datum'] = registration.dateOfBirth ? new Date(registration.dateOfBirth).toLocaleDateString('de-DE') : '-';
          rowData['Geburtsort'] = registration.placeOfBirth || '-';
        }

        excelData.push(rowData);
      });
    });

    // Getränke-Statistik erstellen
    const drinkCounts = {};
    game.registrations.forEach(registration => {
      const drinks = registration.drink ?
        (typeof registration.drink === 'string' ? registration.drink.split(',') : [registration.drink])
        : [];
      drinks.forEach(drink => {
        const drinkLabel = getDrinkLabel(drink.trim());
        drinkCounts[drinkLabel] = (drinkCounts[drinkLabel] || 0) + 1;
      });
    });

    // ALLES IN EIN SHEET: Zusammenfassung oben, dann Anmeldungen darunter
    const isChampionsLeague = game.category === 'champions-league';
    const emptyRow = isChampionsLeague
      ? { 'A': '', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '', 'K': '', 'L': '', 'M': '' }
      : { 'A': '', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '' };

    const combinedData = [
      // ZUSAMMENFASSUNG
      isChampionsLeague
        ? { 'A': 'ZUSAMMENFASSUNG', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '', 'K': '', 'L': '', 'M': '' }
        : { 'A': 'ZUSAMMENFASSUNG', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '' },
      emptyRow,
      { 'A': 'Spiel:', 'B': game.opponent },
      { 'A': 'Datum:', 'B': `${new Date(game.date).toLocaleDateString('de-DE')} - ${game.time}` },
      { 'A': 'Anmeldungen:', 'B': `${game.registrations.length}/${game.maxRegistrations}` },
      emptyRow,
      { 'A': 'Status:', 'B': '' },
      { 'A': '  Bestätigt:', 'B': game.registrations.filter(r => r.status === 'confirmed').length },
      { 'A': '  Ausstehend:', 'B': game.registrations.filter(r => r.status === 'pending').length },
      emptyRow,
      { 'A': 'Tickets:', 'B': '' },
      { 'A': '  Stehplätze:', 'B': game.registrations.filter(r => r.ticketType === 'stehplatz').length },
      { 'A': '  Sitzplätze:', 'B': game.registrations.filter(r => r.ticketType === 'sitzplatz').length },
      { 'A': '  Ohne Ticket:', 'B': game.registrations.filter(r => r.ticketType === 'ohne-ticket').length },
      emptyRow,
      { 'A': 'Getränke:', 'B': '' },
      ...Object.entries(drinkCounts).map(([drink, count]) => ({
        'A': `  ${drink}:`,
        'B': count
      })),
      emptyRow,
      emptyRow,
      // ANMELDUNGEN HEADER
      isChampionsLeague
        ? { 'A': 'Nr.', 'B': 'Gruppe', 'C': 'Vorname', 'D': 'Nachname', 'E': 'E-Mail', 'F': 'Ticket-Art', 'G': 'Getränke', 'H': 'Status', 'I': 'Angemeldet am', 'J': 'Adresse', 'K': 'Perso-Nr', 'L': 'Geb. Datum', 'M': 'Geburtsort' }
        : { 'A': 'Nr.', 'B': 'Gruppe', 'C': 'Vorname', 'D': 'Nachname', 'E': 'E-Mail', 'F': 'Ticket-Art', 'G': 'Getränke', 'H': 'Status', 'I': 'Angemeldet am' },
      // ANMELDUNGEN DATEN
      ...excelData.map(row => {
        const baseData = {
          'A': row['Nr.'],
          'B': row['Gruppe'],
          'C': row['Vorname'],
          'D': row['Nachname'],
          'E': row['E-Mail'],
          'F': row['Ticket-Art'],
          'G': row['Getränke'],
          'H': row['Status'],
          'I': row['Angemeldet am']
        };

        if (isChampionsLeague) {
          baseData['J'] = row['Adresse'] || '-';
          baseData['K'] = row['Perso-Nr'] || '-';
          baseData['L'] = row['Geb. Datum'] || '-';
          baseData['M'] = row['Geburtsort'] || '-';
        }

        return baseData;
      })
    ];

    // Workbook und Sheet erstellen
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(combinedData, { skipHeader: true });

    // Column Widths setzen
    const columnWidths = [
      { wch: 20 }, // A: Nr. / Info
      { wch: 15 }, // B: Gruppe / Wert
      { wch: 20 }, // C: Vorname
      { wch: 20 }, // D: Nachname
      { wch: 35 }, // E: E-Mail
      { wch: 15 }, // F: Ticket-Art
      { wch: 25 }, // G: Getränke
      { wch: 12 }, // H: Status
      { wch: 15 }  // I: Angemeldet am
    ];

    if (isChampionsLeague) {
      columnWidths.push(
        { wch: 40 }, // J: Adresse
        { wch: 15 }, // K: Perso-Nr
        { wch: 12 }, // L: Geb. Datum
        { wch: 20 }  // M: Geburtsort
      );
    }

    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Anmeldungsliste');

    // Datei speichern
    const fileName = `Anmeldungsliste_${game.opponent}_${new Date(game.date).toLocaleDateString('de-DE')}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success('Anmeldungsliste wurde als Excel exportiert!');
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'bundesliga': 'Bundesliga',
      'champions-league': 'Champions League',
      'dfb-pokal': 'DFB-Pokal',
      'europa-league': 'Europa League'
    };
    return categories[category] || category;
  };

  const getFilteredAndSortedGames = () => {
    let filtered = games.filter(game => {
      const matchesSearch = game.opponent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           game.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || game.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'opponent':
          comparison = a.opponent.localeCompare(b.opponent);
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'registrations':
          comparison = (a.registrations?.length || 0) - (b.registrations?.length || 0);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const handleSelectGame = (gameId) => {
    setSelectedGames(prev => 
      prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId]
    );
  };

  const handleSelectAllGames = () => {
    const filteredGames = getFilteredAndSortedGames();
    if (selectedGames.length === filteredGames.length) {
      setSelectedGames([]);
    } else {
      setSelectedGames(filteredGames.map(game => game.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGames.length === 0) return;

    if (window.confirm(`Möchten Sie wirklich ${selectedGames.length} Spiel(e) löschen?`)) {
      try {
        await Promise.all(selectedGames.map(gameId => gameAPI.deleteGame(gameId)));
        toast.success(`${selectedGames.length} Spiel(e) wurden gelöscht!`);
        setSelectedGames([]);
        await loadGames();
      } catch (error) {
        console.error('Fehler beim Löschen der Spiele:', error);
        toast.error('Fehler beim Löschen der Spiele');
      }
    }
  };

  const getGameStatus = (game) => {
    const gameDate = new Date(game.date);
    const now = new Date();
    const registrationCount = game.registrations?.length || 0;
    const maxRegistrations = game.maxRegistrations || 0;

    if (gameDate < now) {
      return { status: 'past', label: 'Vergangen', color: 'gray' };
    } else if (registrationCount >= maxRegistrations) {
      return { status: 'full', label: 'Ausgebucht', color: 'red' };
    } else if (registrationCount > maxRegistrations * 0.8) {
      return { status: 'almost-full', label: 'Fast voll', color: 'orange' };
    } else {
      return { status: 'open', label: 'Verfügbar', color: 'green' };
    }
  };

  return (
    <div className="games-tab">
      {/* Controls */}
      <div className="admin-controls">
        <div className="admin-actions">
          <button
            onClick={() => setShowAddGame(true)}
            className="btn btn-primary"
          >
            + Neues Spiel hinzufügen
          </button>
          {selectedGames.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger"
            >
              {selectedGames.length} Spiel(e) löschen
            </button>
          )}
        </div>

        {/* Filter */}
        <div className="admin-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Suche nach Gegner oder Ort..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Alle Wettbewerbe</option>
              <option value="bundesliga">Bundesliga</option>
              <option value="champions-league">Champions League</option>
              <option value="dfb-pokal">DFB-Pokal</option>
              <option value="europa-league">Europa League</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="date">Nach Datum</option>
              <option value="opponent">Nach Gegner</option>
              <option value="location">Nach Ort</option>
              <option value="registrations">Nach Anmeldungen</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="sort-order-btn"
              title={sortOrder === 'asc' ? 'Aufsteigend' : 'Absteigend'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <div className="view-toggle">
            <button
              onClick={() => setViewMode('table')}
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
            >
              Tabelle
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
            >
              Karten
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content">
        {loading ? (
          <div className="loading"><p>Lade Spiele...</p></div>
        ) : (
          <>
            {/* Stats */}
            <div className="games-stats">
              <div className="stat-card"><h3>{games.length}</h3><p>Gesamt Spiele</p></div>
              <div className="stat-card"><h3>{games.filter(g => getGameStatus(g).status === 'open').length}</h3><p>Verfügbar</p></div>
              <div className="stat-card"><h3>{games.filter(g => getGameStatus(g).status === 'full').length}</h3><p>Ausgebucht</p></div>
              <div className="stat-card"><h3>{games.reduce((sum, g) => sum + (g.registrations?.length || 0), 0)}</h3><p>Gesamt Anmeldungen</p></div>
            </div>

            {/* Tabelle oder Kartenansicht */}
            {viewMode === 'table' ? (
              <div className="games-table-wrapper">
                <table className="games-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedGames.length === getFilteredAndSortedGames().length && getFilteredAndSortedGames().length > 0}
                          onChange={handleSelectAllGames}
                        />
                      </th>
                      <th>Status</th>
                      <th>Gegner</th>
                      <th>Datum & Zeit</th>
                      <th>Ort</th>
                      <th>Wettbewerb</th>
                      <th>Anmeldungen</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredAndSortedGames().map((game) => {
                      const status = getGameStatus(game);
                      return (
                        <tr key={game.id} className={selectedGames.includes(game.id) ? 'selected' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedGames.includes(game.id)}
                              onChange={() => handleSelectGame(game.id)}
                            />
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge status-${status.status}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="opponent-cell">
                            {game.opponent}
                          </td>
                          <td>
                            <div className="date-time">
                              <div className="date">{new Date(game.date).toLocaleDateString('de-DE')}</div>
                              <div className="time">{game.time}</div>
                            </div>
                          </td>
                          <td className="location-cell">{game.location}</td>
                          <td className="category-cell">
                            <span className={`category-badge ${game.category}`}>
                              {getCategoryLabel(game.category)}
                            </span>
                          </td>
                          <td className="registrations-cell">
                            <div className="registrations-info">
                              <div className="count">
                                {game.registrations ? game.registrations.length : 0}/{game.maxRegistrations}
                                <br />
                                <small style={{color: '#28a745', fontSize: '0.8em'}}>
                                  {game.registrations ? game.registrations.filter(r => r.status === 'confirmed').length : 0} bestätigt
                                </small>
                              </div>
                              <div className="progress-bar">
                                <div 
                                  className={`progress-fill status-${status.status}`}
                                  style={{
                                    width: `${Math.min(100, ((game.registrations?.length || 0) / game.maxRegistrations) * 100)}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button
                                onClick={() => setSelectedGame(game)}
                                className="btn btn-sm btn-secondary"
                                title="Anmeldungen anzeigen"
                              >
                                👁️
                              </button>
                              <button
                                onClick={() => generatePDF(game)}
                                className="btn btn-sm btn-primary"
                                disabled={!game.registrations || game.registrations.length === 0}
                                title="PDF Export"
                              >
                                📄
                              </button>
                              <button
                                onClick={() => generateExcel(game)}
                                className="btn btn-sm btn-success"
                                disabled={!game.registrations || game.registrations.length === 0}
                                title="Excel Export"
                              >
                                📊
                              </button>
                              <button
                                onClick={() => handleDeleteGame(game.id)}
                                className="btn btn-sm btn-danger"
                                title="Löschen"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="games-grid">
                {getFilteredAndSortedGames().map((game) => {
                  const status = getGameStatus(game);
                  return (
                    <div key={game.id} className={`game-card ${selectedGames.includes(game.id) ? 'selected' : ''}`}>
                      <div className="card-header">
                        <input
                          type="checkbox"
                          checked={selectedGames.includes(game.id)}
                          onChange={() => handleSelectGame(game.id)}
                          className="card-checkbox"
                        />
                        <span className={`status-badge ${status.status}`} style={{backgroundColor: status.color}}>
                          {status.label}
                        </span>
                      </div>
                      <div className="game-info">
                        <h3>{game.opponent}</h3>
                        <p className="game-date">
                          {new Date(game.date).toLocaleDateString('de-DE')} um {game.time}
                        </p>
                        <p className="game-location">📍 {game.location}</p>
                        <span className={`category-badge ${game.category}`}>
                          {getCategoryLabel(game.category)}
                        </span>
                        <div className="game-stats">
                          <div className="registrations-info">
                            <span className="count">
                              {game.registrations ? game.registrations.length : 0}/{game.maxRegistrations} Anmeldungen
                              <br />
                              <small style={{color: '#28a745'}}>
                                {game.registrations ? game.registrations.filter(r => r.status === 'confirmed').length : 0} bestätigt
                              </small>
                            </span>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{
                                  width: `${Math.min(100, ((game.registrations?.length || 0) / game.maxRegistrations) * 100)}%`,
                                  backgroundColor: status.color
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="game-actions">
                        <button
                          onClick={() => setSelectedGame(game)}
                          className="btn btn-secondary btn-sm"
                        >
                          Anmeldungen anzeigen
                        </button>
                        <button
                          onClick={() => generatePDF(game)}
                          className="btn btn-primary btn-sm"
                          disabled={!game.registrations || game.registrations.length === 0}
                        >
                          📄 PDF
                        </button>
                        <button
                          onClick={() => generateExcel(game)}
                          className="btn btn-success btn-sm"
                          disabled={!game.registrations || game.registrations.length === 0}
                        >
                          📊 Excel
                        </button>
                        <button
                          onClick={() => handleDeleteGame(game.id)}
                          className="btn btn-danger btn-sm"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Keine Spiele */}
            {getFilteredAndSortedGames().length === 0 && (
              <div className="empty-state">
                <p>
                  {searchTerm || categoryFilter !== 'all'
                    ? 'Keine Spiele gefunden, die den Filterkriterien entsprechen.'
                    : 'Noch keine Spiele vorhanden.'}
                </p>
                {(searchTerm || categoryFilter !== 'all') && (
                  <button
                    onClick={() => { setSearchTerm(''); setCategoryFilter('all'); }}
                    className="btn btn-secondary"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Anmeldungsdetails Modal */}
      {selectedGame && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Anmeldungen für {selectedGame.opponent}</h2>
              <button
                onClick={() => setSelectedGame(null)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <div className="registrations-summary">
              <div className="summary-item">
                <strong>Spiel:</strong> {selectedGame.opponent}
              </div>
              <div className="summary-item">
                <strong>Datum:</strong> {new Date(selectedGame.date).toLocaleDateString('de-DE')} um {selectedGame.time}
              </div>
              <div className="summary-item">
                <strong>Ort:</strong> {selectedGame.location}
              </div>
              <div className="summary-item">
                <strong>Anmeldungen:</strong> {selectedGame.registrations ? selectedGame.registrations.length : 0}/{selectedGame.maxRegistrations}
                <span style={{color: '#28a745', marginLeft: '10px'}}>
                  ({selectedGame.registrations ? selectedGame.registrations.filter(r => r.status === 'confirmed').length : 0} bestätigt)
                </span>
              </div>
            </div>

{selectedGame.registrations && selectedGame.registrations.length > 0 ? (
              <div className="registrations-table-wrapper">
                <table className="registrations-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>E-Mail</th>
                      <th>Ticket-Art</th>
                      <th>Getränk</th>
                      <th>Status</th>
                      <th>Angemeldet am</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Gruppiere Registrierungen nach user_id
                      const groupedRegistrations = selectedGame.registrations.reduce((groups, registration) => {
                        const userId = registration.userId;
                        if (!groups[userId]) {
                          groups[userId] = [];
                        }
                        groups[userId].push(registration);
                        return groups;
                      }, {});

                      let rowIndex = 0;
                      let groupColorIndex = 0;
                      const groupColors = ['#e3f2fd', '#f3e5f5', '#e8f5e8', '#fff3e0'];
                      const borderColors = ['#1976d2', '#7b1fa2', '#388e3c', '#f57c00'];
                      
                      return Object.values(groupedRegistrations).map((userRegistrations) => {
                        const isGroup = userRegistrations.length > 1;
                        const currentGroupColor = isGroup ? groupColors[groupColorIndex % 4] : 'transparent';
                        const currentBorderColor = isGroup ? borderColors[groupColorIndex % 4] : 'none';
                        
                        if (isGroup) {
                          groupColorIndex++;
                        }
                        
                        return userRegistrations.map((registration, groupIndex) => {
                          rowIndex++;
                          
                          return (
                            <tr 
                              key={registration.id} 
                              style={{
                                backgroundColor: currentGroupColor,
                                borderLeft: isGroup ? `4px solid ${currentBorderColor}` : 'none'
                              }}
                            >
                              <td>
                                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                  {rowIndex}
                                  {isGroup && (
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '12px',
                                      color: '#666'
                                    }}>
                                      <span style={{
                                        background: '#007bff',
                                        color: 'white',
                                        borderRadius: '10px',
                                        padding: '2px 6px',
                                        marginLeft: '4px',
                                        fontSize: '10px'
                                      }}>
                                        {groupIndex + 1}/{userRegistrations.length}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{registration.name}</td>
                              <td>{registration.email}</td>
                              <td>{getTicketTypeLabel(registration.ticketType)}</td>
                              <td>{getDrinkLabel(registration.drink || 'bier')}</td>
                              <td>
                                <span className={`status ${registration.status}`}>
                                  {registration.status === 'pending' ? 'Ausstehend' :
                                   registration.status === 'confirmed' ? 'Bestätigt' :
                                   registration.status === 'cancelled' ? 'Abgelehnt' : registration.status}
                                </span>
                              </td>
                              <td>{registration.registeredAt ? new Date(registration.registeredAt).toLocaleDateString('de-DE') : '-'}</td>
                              <td>
                                <div className="registration-actions">
                                  <button
                                    onClick={() => handleApproveRegistration(registration.id, selectedGame.opponent)}
                                    className={`btn btn-sm ${registration.status === 'confirmed' ? 'btn-success active' : 'btn-outline-success'}`}
                                    disabled={processingRegistrations.has(registration.id) || registration.status === 'confirmed'}
                                    title={registration.status === 'confirmed' ? 'Bereits bestätigt' : 'Anmeldung bestätigen'}
                                  >
                                    {processingRegistrations.has(registration.id) ? '...' : '✓'}
                                  </button>
                                  <button
                                    onClick={() => handleRejectRegistration(registration.id, selectedGame.opponent)}
                                    className={`btn btn-sm ${registration.status === 'cancelled' ? 'btn-danger active' : 'btn-outline-danger'}`}
                                    disabled={processingRegistrations.has(registration.id) || registration.status === 'cancelled'}
                                    title={registration.status === 'cancelled' ? 'Bereits abgelehnt' : 'Anmeldung ablehnen'}
                                  >
                                    {processingRegistrations.has(registration.id) ? '...' : '✗'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      }).flat();
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-registrations">
                <p>Noch keine Anmeldungen für dieses Spiel.</p>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => generatePDF(selectedGame)}
                className="btn btn-primary"
                disabled={!selectedGame.registrations || selectedGame.registrations.length === 0}
              >
                📄 PDF Export
              </button>
              <button
                onClick={() => generateExcel(selectedGame)}
                className="btn btn-success"
                disabled={!selectedGame.registrations || selectedGame.registrations.length === 0}
              >
                📊 Excel Export
              </button>
              <button
                onClick={() => setSelectedGame(null)}
                className="btn btn-secondary"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spiel hinzufügen Modal */}
      {showAddGame && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Neues Spiel hinzufügen</h2>
              <button
                onClick={() => setShowAddGame(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddGame} className="game-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="opponent">Gegner *</label>
                  <input
                    type="text"
                    id="opponent"
                    name="opponent"
                    value={gameForm.opponent}
                    onChange={handleGameFormChange}
                    required
                    placeholder="z.B. FC Bayern München"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="category">Wettbewerb</label>
                  <select
                    id="category"
                    name="category"
                    value={gameForm.category}
                    onChange={handleGameFormChange}
                  >
                    <option value="bundesliga">Bundesliga</option>
                    <option value="champions-league">Champions League</option>
                    <option value="dfb-pokal">DFB-Pokal</option>
                    <option value="europa-league">Europa League</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date">Datum *</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={gameForm.date}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="time">Uhrzeit *</label>
                  <input
                    type="time"
                    id="time"
                    name="time"
                    value={gameForm.time}
                    onChange={handleGameFormChange}
                    required
                  />
                </div>
              </div>

              {/* Location-Feld entfernt - wird automatisch auf "EFC Buseck" gesetzt */}

              <div className="form-group">
                <label htmlFor="maxRegistrations">Max. Anmeldungen</label>
                <input
                  type="number"
                  id="maxRegistrations"
                  name="maxRegistrations"
                  value={gameForm.maxRegistrations}
                  onChange={handleGameFormChange}
                  min="1"
                  max="200"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Beschreibung</label>
                <textarea
                  id="description"
                  name="description"
                  value={gameForm.description}
                  onChange={handleGameFormChange}
                  rows="3"
                  placeholder="Zusätzliche Informationen zum Spiel..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowAddGame(false)}
                  className="btn btn-secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Erstelle...' : 'Spiel hinzufügen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamesTab;
